import {
    addMonths,
    addSeconds,
    differenceInDays,
    differenceInSeconds,
    eachMonthOfInterval,
    endOfMonth,
    isSameQuarter,
    isSameYear,
    startOfMonth
} from 'date-fns';
import { Component, DateType, component, property } from '../../../../concepts/objects';
import { memoUsing } from '../../../../concepts/objects/MemoUsing';
import { Matrix } from '../../../../platform/geom/Matrix';
import { Point } from '../../../../platform/geom/Point';
import { Rect } from '../../../../platform/geom/Rect';
import { GuideLine, PotentialInteraction, RelativeTo } from '../../../canvas/entities/Physical';
import { AutoLayoutMode } from '../../autoLayout/AutoLayout';
import { Frame } from '../Frame';
import { FrameAddon } from '../FrameAddon';
import { GridLayout, LayoutGroup } from '../Layout';
import { DimensionsType } from './DimensionsType';
import { FrameAddonWithDimensions } from './FrameAddonWithDimensions';
import { TimelineFrameAddonPropertyLayout } from './TimelineFrameAddonPropertyLayout';

export const TimelineFrameAddonLayout = (object: TimelineFrameAddon) => {
    const { base } = object.parentFrame.layout;

    const start = startOfMonth(object.from);
    const end = startOfMonth(object.to);

    const showYears = object.headers.includes('Y');
    const showQuarters = object.headers.includes('Q');
    const showMonths = object.headers.includes('M');

    const hs = 50;
    const top = new Rect(0, 0, base.width, 0);

    const monthValues = eachMonthOfInterval({ start, end }).map(x => ({ date: x }));
    const [columns, rows] = object.dimensions;

    const years = GridLayout.merge(
        GridLayout.from(
            top.set({ height: showYears ? hs : 0 }),
            columns,
            showYears ? monthValues : [],
        ),
        (a, b) => isSameYear(a.date, b.date),
    );

    const quarters = GridLayout.merge(
        GridLayout.from(
            top.set({ top: years.area.bottom, height: showQuarters ? hs : 0 }),
            columns,
            showQuarters ? monthValues : [],
        ),
        (a, b) => isSameQuarter(a.date, b.date),
    );

    const months = GridLayout.from(
        top.set({ top: quarters.area.bottom, height: showMonths ? hs : 0 }),
        columns,
        showMonths ? monthValues : [],
    );

    const dateline = object.headers.includes('D');
    const now = differenceInDays(new Date(), start) / differenceInDays(endOfMonth(end), start);

    const head = { ...LayoutGroup.from({ years, quarters, months }), dateline };
    const body = top.set({ top: months.area.bottom, height: base.height - months.area.bottom });

    const pattern = {
        width: body.width / columns,
        height: body.height / rows,
        // x: pw * .5,
        // y: ph * .5,
    };

    return { base, head, body, pattern, columns, rows, now };
};

export type TimelineFrameAddonLayout = ReturnType<typeof TimelineFrameAddonLayout>;

export type TimelineFrameAddonHeadingToken = 'Y' | 'Q' | 'M' | 'D';
export type TimelineFrameAddonHeadings = TimelineFrameAddonHeadingToken[];

const timelineFrameAddonDef = {
    name: 'TimelineFrameAddon',
    implements: [FrameAddon, FrameAddonWithDimensions],
    propertyLayout: TimelineFrameAddonPropertyLayout,
};

@component(timelineFrameAddonDef)
export class TimelineFrameAddon extends Component implements FrameAddon, FrameAddonWithDimensions {

    public get parentFrame() {
        return this.parent?.as(Frame);
    }

    @memoUsing('from!', 'to!', 'headers', 'dimensions!', 'parentFrame.layout')
    public get layout() {
        return TimelineFrameAddonLayout(this);
    }

    public get layer() {
        return 'bottom' as const;
    }

    @memoUsing('from!', 'dimensions!')
    public get to() {
        const months = this.dimensions[0] || 1;
        return addMonths(this.from, months - 1);
    }

    @property({ type: 'string', style: true })
    public color: string;

    @property({ type: DateType })
    public from: Date;

    @property({ type: 'string[]', default: ['Y', 'Q', 'M'] })
    public headers: TimelineFrameAddonHeadings;

    @property({ type: DimensionsType, default: [5, 5] })
    public dimensions: [number, number];

    @property({ type: 'string[]', default: AutoLayoutMode.none })
    public layoutMode: AutoLayoutMode;

    public getDateAtPosition(mx: number) {
        if (mx === null || mx === undefined) return null;
        const { from } = this;
        const rx = mx - this.parentFrame.position(RelativeTo.Model).x;
        const col = Math.floor(rx / (this.parentFrame.width / this.dimensions[0]));
        const colStartX = col * (this.parentFrame.width / this.dimensions[0]);
        const colEndX = colStartX + (this.parentFrame.width / this.dimensions[0]);
        const colPerc = (rx - colStartX) / (colEndX - colStartX);
        const colStartDate = addMonths(from, col);
        const colEndDate = addMonths(colStartDate, 1);
        const colPos = addSeconds(colStartDate, differenceInSeconds(colEndDate, colStartDate) * colPerc);
        return colPos;
    }

    public layoutCells(mt: Matrix) {

        const cells = [] as Rect[];
        const { body, columns, rows } = this.layout;
        const { w, h } = body.size().divide([columns, rows]);

        for (let c = 0; c < columns; c++) {
            for (let r = 0; r < rows; r++) {
                cells.push(Rect.fromPoints(mt.apply(new Rect(body.left + (w * c), body.top + (h * r), w, h))));
            }
        }

        return cells;
    }

    public offsetDimensions(cc: number, rc: number): void {
        if (cc !== 0) {
            this.from = addMonths(this.from, cc * -1);
        }
    }

    public getCorrectedSize(w: number, h: number): Point {
        const { head, columns, rows } = this.layout;
        const dx = columns * 10;
        const dy = rows * 10;
        const hh = head.area.height;

        return new Point(
            Math.round(w / dx) * dx,
            hh + (Math.round((h - hh) / dy) * dy),
        );
    }

    public queryGuides(): GuideLine[] {
        const rects = this.layoutCells(this.parentFrame.transform(RelativeTo.Model));
        return rects.flatMap(x => GuideLine.fromRect(x));
    }

    public test(mpt: Point): boolean {
        const { head } = this.layout;
        const lpt = this.parentFrame.toRelative(mpt);
        return head.area.contains(lpt);
    }

    public testInteraction({ type, mpt }: PotentialInteraction): [boolean, string] {
        const { head } = this.layout;
        const lpt = this.parentFrame.toRelative(mpt);

        if (type === 'select' && head.area.contains(lpt)) {
            return [true, 'head'];
        }

        return [false, 'none'];
    }
}
